import React, { useRef, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BottomToolbar from "./BottomToolbar";
import {
  Box,
  Container,
  Fab,
  TextField,
  IconButton,
  Paper,
  Typography,
  Button,
  Zoom,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  Stack,
  InputAdornment,
} from "@mui/material";
import { Search as SearchIcon } from "../icons";
import { ArrowBack as ArrowBackIcon } from "../icons";
import { ChevronRight as ChevronRightIcon } from "../icons";
import { ChevronLeft as ChevronLeftIcon } from "../icons";
import { Close as CloseIcon } from "../icons";
import { SentimentDissatisfied as SentimentDissatisfiedIcon } from "../icons";
import { CheckCircle as CheckCircleIcon } from "../icons";
import { barColors, brandColors } from "../theme";
import { alpha } from "@mui/material/styles";
import { pageContentSx } from "../pageLayout";

import SearchClearAdornment from "../components/SearchClearAdornment";
const UniversalSearch = ({
  data = [],
  onSearchResults = () => {},
  onClearSearch = () => {}, // Nueva prop para manejar la limpieza
  searchKeys = ['name', 'title', 'description', 'product'],
  placeholder = "Buscar producto o tienda",
  noResultsTitle = "No encontramos resultados",
  noResultsMessage = "No hay productos o tiendas que coincidan con tu búsqueda",
  buttonPosition = { top: 120, right: 16 },
  zIndex = 1000,
  showBottomToolbar = true,
  // Cuando se pasan categorías (ver members.js), este componente deja de ser
  // el FAB de búsqueda flotante y pasa a ser una appbar persistente (barra de
  // búsqueda + fila de tabs de categoría debajo), igual que StoresAppBar.js.
  // Sin categories, se comporta exactamente igual que antes (ver viajes.js).
  categories,
  categoryField,
  onCategoryChange,
}) => {
  const navigate = useNavigate();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const searchTimeout = useRef(null);
  const [filteredResults, setFilteredResults] = useState([]);
  const [searchActive, setSearchActive] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const categoryScrollRef = useRef(null);

  const scrollCategoriesRight = () => {
    categoryScrollRef.current?.scrollBy({ left: 150, behavior: "smooth" });
  };

  const scrollCategoriesLeft = () => {
    categoryScrollRef.current?.scrollBy({ left: -150, behavior: "smooth" });
  };

  

  const styles = {
    searchFab: {
      position: "fixed",
      zIndex: zIndex,
      ...buttonPosition,
      backgroundColor: (theme) => theme.palette.primary.main
    },
    searchContainer: {
      position: "fixed",
      zIndex: zIndex,
      display: "flex",
      alignItems: "center",
      backgroundColor: brandColors.backgroundPaper,
      borderRadius: "24px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      padding: "8px 16px",
      transition: "all 0.3s ease",
      ...buttonPosition,
    },
    searchInput: {
      marginRight: "8px",
      border: "none",
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
    noResults: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "2rem",
      textAlign: "center",
      marginTop: "2rem",
      backgroundColor: brandColors.backgroundPaper,
      borderRadius: "8px",
      gap: "1rem",
    },
    noResultsIcon: {
      fontSize: "4rem",
      color: "#9e9e9e",
    },
    resultsFound: {
      position: "fixed",
      top: buttonPosition.top + 60,
      right: buttonPosition.right,
      zIndex: 1000,
      backgroundColor: "#4caf50",
      color: "white",
      padding: "8px 16px",
      borderRadius: "4px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    },
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      handleSearch(value);
    }, 300);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const normalizeText = (text) => {
    if (!text) return '';
    return text.toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  };

  const handleSearch = useCallback((searchValue) => {
    if (!Array.isArray(data)) {
      console.error("data is not an array:", data);
      return;
    }

    const value = normalizeText(searchValue);
    
    if (value === "") {
      onClearSearch();
      setHasSearched(false);
      setShowSnackbar(false);
      setSearchActive(false);
      return;
    }

    setSearchActive(true);
    
    const filtered = data.filter((item) => {
      return searchKeys.some(key => {
        const itemValue = item[key];
        if (!itemValue) return false;
        const normalizedItemValue = normalizeText(itemValue);
        const searchTerms = value.split(/\s+/);
        return searchTerms.every(term => normalizedItemValue.includes(term));
      });
    });

    setFilteredResults(filtered);
    onSearchResults(filtered);
    setHasSearched(true);
    
    if (filtered.length > 0) {
      setShowSnackbar(true);
      setTimeout(() => setShowSnackbar(false), 3000);
    }
  }, [data, searchKeys, onSearchResults, onClearSearch, normalizeText]);

  // Cuando cambia el dataset (ej. se cambia de categoría en members.js, que
  // pasa "data" distinto por cada tab), reaplica el término ya tipeado sobre
  // los nuevos datos en vez de dejar resultados de la categoría anterior.
  const isFirstDataRef = useRef(true);
  useEffect(() => {
    if (isFirstDataRef.current) {
      isFirstDataRef.current = false;
      return;
    }
    if (searchTerm) {
      handleSearch(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      setTimeout(() => {
        document.getElementById('universal-search-input')?.focus();
      }, 100);
    } else {
      setSearchTerm("");
      onClearSearch();
      setHasSearched(false);
      setShowSnackbar(false);
      setSearchActive(false);
    }
  };

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    onClearSearch();
    setHasSearched(false);
    setShowSnackbar(false);
    setSearchActive(false);
  }, [onClearSearch]);

  // Solo mostrar NoResults cuando la búsqueda está activa y no hay resultados
  const shouldShowNoResults = searchActive && hasSearched && searchTerm.length > 0 && filteredResults.length === 0;

  const NoResults = () => (
    <Paper sx={styles.noResults}>
      <SentimentDissatisfiedIcon sx={styles.noResultsIcon} />
      <Typography variant="h6">{noResultsTitle}</Typography>
      <Typography color="textSecondary">
        {noResultsMessage} "{searchTerm}"
      </Typography>
      <Button variant="contained" color="primary" onClick={handleClearSearch}>
        Limpiar búsqueda
      </Button>
    </Paper>
  );

  const ResultsFound = () => (
    <Snackbar
      open={showSnackbar}
      autoHideDuration={3000}
      onClose={() => setShowSnackbar(false)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert icon={<CheckCircleIcon />} severity="success" variant="filled">
        {`Se encontraron ${filteredResults.length} resultado${
          filteredResults.length === 1 ? "" : "s"
        }`}
      </Alert>
    </Snackbar>
  );

  const handleCategoryClick = (value) => {
    const next = activeCategory === value ? null : value;
    setActiveCategory(next);
    onCategoryChange?.(next);
  };

  // Flecha contextual de la appbar: en "Recomendados para ti" (sin
  // categoría activa) vuelve a home; con una categoría activa, vuelve a
  // "Recomendados para ti" en vez de salir de la pantalla.
  const handleBarBack = () => {
    if (activeCategory) {
      setActiveCategory(null);
      onCategoryChange?.(null);
    } else {
      navigate("/home");
    }
  };

  const hasCategoryBar = Array.isArray(categories) && categories.length > 0;
  const activeTabTextColor = brandColors.mode === "light" ? "#ffffff" : "#0a0a1a";
  const categoryButtonSx = (active) => ({
    flex: { xs: "0 0 auto", sm: 1 },
    whiteSpace: "nowrap",
    px: { xs: 1.5, sm: 2 },
    py: 0.75,
    borderRadius: 2,
    textTransform: "none",
    fontSize: { xs: "0.75rem", sm: "0.875rem" },
    color: active ? activeTabTextColor : barColors.textMuted,
    bgcolor: active ? brandColors.accent : "transparent",
    fontWeight: active ? 700 : 500,
    transition: "background-color 0.15s ease, color 0.15s ease",
    "&:hover": {
      color: active ? activeTabTextColor : barColors.text,
      bgcolor: active ? brandColors.accent : barColors.activeBg,
    },
  });

  // Appbar persistente con búsqueda + tabs de categoría (members.js): a
  // diferencia del FAB flotante de abajo, esta va siempre visible arriba.
  if (hasCategoryBar) {
    return (
      <>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{ top: 0, background: barColors.background, color: barColors.text }}
        >
          <Container maxWidth={false} disableGutters sx={pageContentSx}>
            <Toolbar disableGutters sx={{ py: 1, gap: 1 }}>
              <IconButton onClick={handleBarBack} sx={{ color: barColors.text, flexShrink: 0 }}>
                <ArrowBackIcon />
              </IconButton>
              <TextField
                className="notiline-search-tour"
                id="universal-search-input"
                placeholder={placeholder}
                fullWidth
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  endAdornment: <SearchClearAdornment value={searchTerm} onChange={handleSearchChange} />,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: barColors.textMuted }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "24px",
                    backgroundColor: alpha(barColors.text, 0.12),
                    color: barColors.text,
                  },
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "& .MuiInputBase-input::placeholder": {
                    color: barColors.textMuted,
                    opacity: 1,
                  },
                }}
              />
            </Toolbar>
            <Box sx={{ position: "relative", borderTop: `1px solid ${barColors.border}` }}>
              <Stack
                ref={categoryScrollRef}
                direction="row"
                sx={{ overflowX: "auto", py: 0.75, gap: 0.75, px: 4 }}
              >
                {categories.map((cat) => (
                  <Button
                    key={cat.value}
                    startIcon={cat.icon}
                    onClick={() => handleCategoryClick(cat.value)}
                    sx={categoryButtonSx(activeCategory === cat.value)}
                  >
                    {cat.label}
                  </Button>
                ))}
              </Stack>
              <Box
                onClick={scrollCategoriesLeft}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  cursor: "pointer",
                  background: `linear-gradient(to left, ${alpha(barColors.background, 0)}, ${barColors.background} 70%)`,
                }}
              >
                <ChevronLeftIcon sx={{ color: barColors.textMuted }} />
              </Box>
              <Box
                onClick={scrollCategoriesRight}
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  cursor: "pointer",
                  background: `linear-gradient(to right, ${alpha(barColors.background, 0)}, ${barColors.background} 70%)`,
                }}
              >
                <ChevronRightIcon sx={{ color: barColors.textMuted }} />
              </Box>
            </Box>
          </Container>
        </AppBar>
        {shouldShowNoResults && <NoResults />}
        <ResultsFound />
        {showBottomToolbar && <BottomToolbar />}
      </>
    );
  }

  return (
    <>
      <Box>
      <Zoom in={!isSearchOpen}>
        <Fab
          color="primary"
          aria-label="search"
          onClick={toggleSearch}
          sx={styles.searchFab}
        >
          <SearchIcon />
        </Fab>
      </Zoom>

      <Zoom in={isSearchOpen}>
        <Paper sx={{ ...styles.searchContainer, minWidth: "280px" }}>
          <TextField
            className="notiline-search-tour"
            id="universal-search-input"
            placeholder={placeholder}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={styles.searchInput}
          />
          <IconButton onClick={toggleSearch} size="small">
            <CloseIcon />
          </IconButton>
        </Paper>
      </Zoom>

      {shouldShowNoResults && <NoResults />}
      <ResultsFound />
    </Box>
    {showBottomToolbar && <BottomToolbar />}
    </>
  );
};

export default UniversalSearch;
